// Sign up or sign in (ACCOUNTS.md): one sheet, four fields, no wizard, no
// verification wall. The email is explained as changeable right under the
// field, because that is the promise the account model makes. The time zone
// is the account's own from the start (B-181). A forgotten password is a
// third state of the same sheet, reached from signing in (B-306), and a
// sign-in code by email a fourth (PLAN-2026-10-28-by-email §3.4, B-547).
//
// THE CODE, NOT ONLY THE LINK. A link in an email opens whichever browser the
// mail app picks, and an app installed on an iPhone keeps its own storage, so
// the link signs Safari in and leaves the app at this sheet (INSTALL.md §1).
// The six digits are typed where they were asked for, and the sheet says so
// before the email is sent, more plainly inside an installed app.

import { useEffect, useState } from 'react';
import { newPasswordRefusal, PASSWORD_RULE } from './passwordRule';
import { Button, Checkbox, Dialog, PasswordField, Stack, Text, TextField } from '../../kit/ui';
import { useSession } from '../../data/session';
import { deviceZone } from '../../data/hooks';
import { TimeZoneSelect } from './TimeZoneSelect';

export type SignUpMode = 'sign-up' | 'sign-in';

/**
 * Why this sheet appeared, and what happens once it is done.
 *
 * Tapping Start a league while signed out once threw up a sign-up popup with
 * no explanation, which only confused. A form that arrives with no reason
 * reads as an obstacle. Every door that
 * opens this sheet says what it is for and what comes next.
 */
export interface SignUpReason {
  /** The requirement, plainly. */
  needs: string;
  /** What the person came to do, once the account exists. */
  then: string;
}

export function SignUpSheet({
  mode,
  opened,
  onClose,
  onDone,
  suggestedName,
  reason,
}: {
  mode: SignUpMode;
  opened: boolean;
  onClose: () => void;
  /** Called with the fresh session; the caller decides where to land. */
  onDone: () => void;
  suggestedName?: string;
  reason?: SignUpReason;
}) {
  const { signUp, signIn, requestReset, requestCode, signInWithCode } = useSession();
  const [displayName, setDisplayName] = useState(suggestedName ?? '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  // THE DEVICE'S ZONE AS THE STARTING CHOICE, shown and changeable, and read
  // this once: after sign-up the account's zone is the only one (B-181). A
  // blank list of four hundred zones is the alternative, for a choice most
  // people would make the same way.
  const [timeZone, setTimeZone] = useState<string>(() => deviceZone());
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  // EITHER WAY ROUND FROM EITHER DOOR: somebody who opens Sign In can still
  // reach Create account, and the reverse. The door picks where the
  // sheet starts; the switch at its foot crosses over.
  const [current, setCurrent] = useState<SignUpMode | 'forgot' | 'code'>(mode);
  // The six digits, once a code has been asked for.
  const [code, setCode] = useState('');
  // Whether the reset link has been asked for, which turns the forgot state
  // into what happens next.
  const [sent, setSent] = useState(false);
  useEffect(() => {
    setCurrent(mode);
    setSent(false);
  }, [mode, opened]);
  const signingUp = current === 'sign-up';
  const go = (to: SignUpMode | 'forgot' | 'code') => {
    setError(null);
    setSent(false);
    setCode('');
    setCurrent(to);
  };
  const cross = () => go(signingUp ? 'sign-in' : 'sign-up');

  /**
   * Ask for the link. The answer is the same whether or not the address has
   * an account, so the next screen says "if" (B-306): it cannot know, and
   * saying "sent" to an address with no account would be a lie that also
   * told a stranger which addresses have one.
   */
  const askForLink = async () => {
    if (pending) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
      return setError('That email does not look right yet.');
    setError(null);
    setPending(true);
    const outcome = await requestReset(email);
    setPending(false);
    if (!outcome.ok) return setError(outcome.message);
    setSent(true);
  };

  /** Ask for a code: the same answer whatever the address, as a reset's. */
  const askForCode = async () => {
    if (pending) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
      return setError('That email does not look right yet.');
    setError(null);
    setPending(true);
    const outcome = await requestCode(email);
    setPending(false);
    if (!outcome.ok) return setError(outcome.message);
    setCode('');
    setSent(true);
  };

  const enterCode = async () => {
    if (pending) return;
    if (!/^\d{6}$/.test(code.replace(/[\s-]/g, '')))
      return setError('The code is the six digits in the email.');
    setError(null);
    setPending(true);
    const outcome = await signInWithCode({ email, code, remember });
    setPending(false);
    if (!outcome.ok) return setError(outcome.message);
    setCode('');
    onDone();
  };

  if (current === 'code') {
    // INSIDE AN INSTALLED APP the link would open the browser instead (§1).
    const installed =
      typeof window !== 'undefined' &&
      (window.matchMedia?.('(display-mode: standalone)').matches === true ||
        (navigator as Navigator & { standalone?: boolean }).standalone === true);
    return (
      <Dialog opened={opened} onClose={onClose} title="Sign in with a code">
        {sent ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              enterCode();
            }}
          >
            <Stack gap={3}>
              <Text block>
                If <strong>{email.trim()}</strong> has an account here, a six-digit code is on its
                way. It works once, for 15 minutes.
              </Text>
              <TextField
                label="Code"
                placeholder="123 456"
                value={code}
                onChange={setCode}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={7}
                code
                autoFocus
              />
              <Text tone="secondary" size="sm" block>
                {installed
                  ? 'Type the code here. The link in the email opens your browser, not this app.'
                  : 'Or tap the link in the email to sign in on the device you read it on.'}
              </Text>
              <Checkbox label="Remember this device" on={remember} onChange={setRemember} />
              {error ? (
                <Text tone="bad" block>
                  {error}
                </Text>
              ) : null}
              <Button variant="primary" submit full loading={pending}>
                Sign in
              </Button>
              <Text size="sm" block center>
                {'Nothing yet? Check your spam folder, or '}
                <button
                  type="button"
                  className="fsp-linklike fsp-signup-cross"
                  onClick={() => {
                    setError(null);
                    setSent(false);
                  }}
                >
                  send a new code
                </button>
                .
              </Text>
              <Text size="sm" block center>
                <button
                  type="button"
                  className="fsp-linklike fsp-signup-cross"
                  onClick={() => go('sign-in')}
                >
                  Use my password
                </button>
              </Text>
            </Stack>
          </form>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              askForCode();
            }}
          >
            <Stack gap={3}>
              <Text tone="secondary" block>
                {installed
                  ? 'We’ll email you a six-digit code. Type it here to sign in to this app.'
                  : 'We’ll email you a six-digit code to type here, and a link that signs in the device you open it on. No password needed.'}
              </Text>
              <TextField
                label="Email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={setEmail}
                autoFocus
              />
              {error ? (
                <Text tone="bad" block>
                  {error}
                </Text>
              ) : null}
              <Button variant="primary" submit full loading={pending}>
                Email me a code
              </Button>
              <Text size="sm" block center>
                <button
                  type="button"
                  className="fsp-linklike fsp-signup-cross"
                  onClick={() => go('sign-in')}
                >
                  Use my password
                </button>
              </Text>
            </Stack>
          </form>
        )}
      </Dialog>
    );
  }

  if (current === 'forgot') {
    return (
      <Dialog opened={opened} onClose={onClose} title="Reset your password">
        {sent ? (
          <Stack gap={3}>
            <Text block>
              If <strong>{email.trim()}</strong> has an account here, a link to choose a new
              password is on its way. It works for one hour.
            </Text>
            <Text tone="secondary" size="sm" block>
              Nothing after a few minutes? Check your spam folder, or send it again.
            </Text>
            <Button variant="primary" full onClick={() => go('sign-in')}>
              Back to sign in
            </Button>
            <Text size="sm" block center>
              <button
                type="button"
                className="fsp-linklike fsp-signup-cross"
                onClick={() => setSent(false)}
              >
                Send it again
              </button>
            </Text>
          </Stack>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              askForLink();
            }}
          >
            <Stack gap={3}>
              <Text tone="secondary" block>
                Type the email you sign in with, and we will send you a link to choose a new
                password.
              </Text>
              <TextField
                label="Email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={setEmail}
                autoFocus
              />
              {error ? (
                <Text tone="bad" block>
                  {error}
                </Text>
              ) : null}
              <Button variant="primary" submit full loading={pending}>
                Email me a link
              </Button>
              <Text size="sm" block center>
                {'Remembered it? '}
                <button
                  type="button"
                  className="fsp-linklike fsp-signup-cross"
                  onClick={() => go('sign-in')}
                >
                  Sign in
                </button>
              </Text>
            </Stack>
          </form>
        )}
      </Dialog>
    );
  }

  /**
   * The checks below are the same ones the server makes, and they run here
   * only so somebody gets an answer without a round trip. **The server's
   * answer wins**: `onDone` is called after it succeeds, never before, or a
   * sheet that closes on a refused sign-up leaves the person looking at a
   * signed-out app with no idea why.
   */
  const submit = async () => {
    if (pending) return;
    const name = displayName.trim();
    const mail = email.trim();
    if (signingUp && name.length < 2) return setError('Give us at least two letters to call you.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail))
      return setError('That email does not look right yet.');
    // Signing in checks nothing: an account's password is whatever it was
    // made with, and the server says only that the pair is wrong.
    const refusal = signingUp ? newPasswordRefusal(password, mail) : null;
    if (refusal !== null) return setError(refusal);
    if (password.length === 0) return setError('Type your password.');
    setError(null);
    setPending(true);
    const outcome = signingUp
      ? await signUp({ displayName: name, email: mail, password, timeZone })
      : await signIn({ email: mail, password, remember });
    setPending(false);
    if (!outcome.ok) {
      // Shown as the server worded it. It is written to be read by the
      // person it happened to: "That email already has an account. Sign in
      // instead." (B-57), or one message covering both halves of a bad
      // sign-in, because which half it was is not the caller's business.
      setError(outcome.message);
      return;
    }
    setPassword('');
    onDone();
  };

  return (
    <Dialog
      opened={opened}
      onClose={onClose}
      title={signingUp ? (reason ? 'First, an account' : 'Create an account') : 'Sign in'}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Stack gap={3}>
          {/* THE REASON SHOWS IN BOTH MODES, and until 2026-09-20 it showed
              only while signing UP. `ACCOUNT_REASON` in `App.tsx` exists for
              exactly one door — asking for `/account` while signed out, which
              is a sign-IN, not a sign-up — and it was passed here and
              dropped, so that door opened a bare form. B-66 calls a sheet
              with no reason, opened by the app rather than by the person,
              one of the ways this goes wrong (AUDIT-2026-09-19 F9). */}
          {reason ? (
            <div className="fsp-signup-reason">
              <div className="fsp-signup-needs">{reason.needs}</div>
              <div className="fsp-sub fsp-ui-wrap-text">{reason.then}</div>
            </div>
          ) : null}
          {signingUp ? (
            <TextField
              label="What should we call you?"
              placeholder="Ken"
              value={displayName}
              onChange={setDisplayName}
              autoFocus
              maxLength={40}
            />
          ) : null}
          <TextField
            label="Email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            description={
              signingUp
                ? 'For signing in and league mail. We’ll email you a link to confirm it’s yours.'
                : undefined
            }
            value={email}
            onChange={setEmail}
            autoFocus={!signingUp}
          />
          <PasswordField
            label="Password"
            description={signingUp ? PASSWORD_RULE : undefined}
            value={password}
            onChange={setPassword}
            autoComplete={signingUp ? 'new-password' : 'current-password'}
          />
          {signingUp ? (
            <TimeZoneSelect
              value={timeZone}
              onChange={setTimeZone}
              description="Every time on the site is shown in this zone, wherever you are. Change it on your Account page."
            />
          ) : null}
          {signingUp ? null : (
            // B-59: staying signed in is a choice somebody makes, and it
            // expires anyway — 90 days with this ticked, 14 without.
            <Checkbox label="Remember this device" on={remember} onChange={setRemember} />
          )}
          {error ? (
            <Text tone="bad" block>
              {error}
            </Text>
          ) : null}
          <Button variant="primary" submit full loading={pending}>
            {signingUp ? "Let's go" : 'Sign in'}
          </Button>
          {/* NO PASSWORD TO REMEMBER (B-547): a code by email, beside it. */}
          {signingUp ? null : (
            <Button variant="secondary" full onClick={() => go('code')}>
              Email me a code instead
            </Button>
          )}
          {signingUp ? (
            <Text tone="secondary" size="xs" block center>
              {/* Who an account is for, where it is made (DIRECTION P6). */}
              Accounts are for people 13 and over. Your account is an id, not your email. We keep a
              display name, an avatar, your time zone, and how you sign in. Nothing else.
            </Text>
          ) : (
            <Text size="sm" block center>
              <button
                type="button"
                className="fsp-linklike fsp-signup-cross"
                onClick={() => go('forgot')}
              >
                Forgot your password?
              </button>
            </Text>
          )}
          <Text size="sm" block center>
            {signingUp ? 'Already have an account? ' : 'New here? '}
            <button type="button" className="fsp-linklike fsp-signup-cross" onClick={cross}>
              {signingUp ? 'Sign in' : 'Create an account'}
            </button>
          </Text>
          {/* WHAT HAPPENS TO THE EMAIL, a press away where it is typed (B-417). */}
          <Text size="sm" tone="secondary" block center>
            {/* In a tab of its own, so what is typed here is still here. */}
            <a
              className="fsp-linklike fsp-signup-privacy"
              href="/privacy"
              target="_blank"
              rel="noopener"
            >
              What we keep, and why
            </a>
          </Text>
        </Stack>
      </form>
    </Dialog>
  );
}
